import { SortDirection } from './../_services/sorting.service';
import { Component, OnInit, Input, Output, EventEmitter, HostListener, ElementRef } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { ProgressReport } from '../graphs/progress-report';
import { Classroom } from '../dropdown-classroom/org.classroom';
import { Student } from '../student-list/student';
import { OrgService } from '../_services/org.service';
import { ReportService } from '../_services/report-service';
import { ResponsiveService } from '../_services/responsive.service';
import { PhaseService } from '../_services/phase.service';
import { LoaderService } from '../loader/loader.service';
import { ScoreHelper, ScoreType } from 'src/app/_helpers/score.type';

interface SortableColumn {
	visible?: boolean;
	selected: boolean;
	isAscending: boolean;
	selectedChallengeId?: number;
	label?: string;
}
@Component({
	selector: 'app-progress-table',
	templateUrl: './progress-table.component.html',
	styleUrls: ['./progress-table.component.css'],
})
export class ProgressTableComponent implements OnInit {
	@Input('phase') targetPhase: number;
	@Input('sorting') parentTableSort: any;
	@Input('defaultSorting') defaultParentTableSort: any;
	@Input('filterText') filterText: string = '';
	@Output() outgoingSorting = new EventEmitter<any>();
	@Output() filterTextChange = new EventEmitter<string>();
	subscriptions: Subscription[] = [];
	public selectedClassroom: Classroom;
	public targetPhaseTitle: string;
	public reportData: ProgressReport;
	public challengeData: any;

	public allColumns: { [key: string]: SortableColumn };

	public leftFooterColumnCount: number;
	public rightFooterColumnCount: number;

	public get hidableColumns() {
		return Object.values(this.allColumns).filter((column) => 'visible' in column && column !== this.allColumns.lastNameColumn);
	}

	public studentData: any;
	public filteredStudentData: any;
	public classSize: number;
	public classPhaseSize: number;
	private classroomId: number;
	private originalStudentData: any;

	public countIndex = 1;
	public scoreType = ScoreType;
	public scoreHelper = ScoreHelper;
	public columnDropdownOpen = false;

	// Column labels mapping
	public columnLabels: { [key: string]: string } = {
		lastNameColumn: 'Last Name',
		firstNameColumn: 'First Name',
		gradeColumn: 'Grade',
		timesAssessedColumn: 'Times Assessed',
		dateAssessedColumn: 'Last Assessment',
	};

	constructor(
		private orgService: OrgService,
		private reportService: ReportService,
		public rsp: ResponsiveService,
		private router: Router,
		public phaseService: PhaseService,
		public LoaderService: LoaderService,
		private elementRef: ElementRef
	) {}

	@HostListener('document:click', ['$event'])
	onDocumentClick(event: MouseEvent): void {
		if (this.columnDropdownOpen) {
			const target = event.target as HTMLElement;
			const button = this.elementRef.nativeElement.querySelector('.column-dropdown-button');
			const dropdown = this.elementRef.nativeElement.querySelector('.column-dropdown-menu');

			// Close if click is outside both button and dropdown
			if (button && !button.contains(target) && dropdown && !dropdown.contains(target)) {
				this.columnDropdownOpen = false;
			}
		}
	}

	public getColumnLabel(columnKey: string): string {
		return this.columnLabels[columnKey] || columnKey;
	}

	public getColumnKey(column: SortableColumn): string {
		for (const key in this.allColumns) {
			if (this.allColumns[key] === column) {
				return key;
			}
		}
		return '';
	}

	ngOnInit(): void {
		this.subscriptions.push(
			this.phaseService.phases$.subscribe((data) => {
				let foundPhase = data.find((phase) => phase.id == this.targetPhase && phase.isActive == 1);
				this.targetPhaseTitle = foundPhase.title;
			})
		);

		this.subscriptions.push(
			this.reportService.progressReport.subscribe((data) => {
				console.log('FETCHED PROGRESS REPORT (in table, this is not the remote download): ', data);
				this.selectedClassroom = data['classroom'];
				this.reportData = data;
				this.challengeData = this.getTargetReportData(data);
				this.classroomId = data['classroom'].id;

				// Set columns first before filtering/sorting
				this.allColumns = this.parentTableSort ? this.parentTableSort : this.defaultParentTableSort;

				// Set student data
				this.originalStudentData = this.setTargetStudentData(data.studentData);
				this.studentData = this.originalStudentData;
				this.classSize = data.studentData.length;
				this.classPhaseSize = this.studentData.length;

				// Set grade column visibility if needed
				if (this.parentTableSort == null) {
					this.SetGradeHomogenous(this.originalStudentData);
				}

				// Apply filter (which will also sort and set footer counts)
				this.applyFilter();
			})
		);
	}

	ngOnChanges() {
		if (this.parentTableSort != null && this.studentData != null) {
			this.allColumns = this.parentTableSort;
			this.startSort();
			this.setFooterColumnCounts();
		}
		// Apply filter when filterText changes, but only if we have data
		if (this.filterText !== undefined && this.originalStudentData) {
			this.applyFilter();
		}
	}

	private setFooterColumnCounts() {
		const allNonScoreColumns = Object.fromEntries(
			Object.entries(this.allColumns).filter(
				([column]) =>
					this.allColumns[column] != this.allColumns.challengeIdColumn &&
					this.allColumns[column] != this.allColumns.allChallengesColumn &&
					this.allColumns[column].visible == true
			)
		);

		const sizeOfMainColumns = Object.keys(allNonScoreColumns).length;
		this.rightFooterColumnCount = Math.floor(sizeOfMainColumns / 2); //With 5 columns or 4 columns, this is 2
		this.leftFooterColumnCount = Math.floor(sizeOfMainColumns / 2) + (sizeOfMainColumns % 2); //With 5 columns, this is 2+1. With 4 columns this is 2+0
	}

	public getTotalColumnCount(): number {
		if (!this.allColumns || !this.challengeData) {
			return 1;
		}

		const visibleMainColumns = Object.values(this.allColumns).filter(
			(column) => column != this.allColumns.challengeIdColumn && column != this.allColumns.allChallengesColumn && column.visible == true
		).length;

		const challengeCount = this.challengeData.challenges?.length || 0;

		return visibleMainColumns + challengeCount;
	}

	private SetGradeHomogenous(studentdata) {
		if (Object.values(studentdata).length != 0) {
			let isHomogenous: boolean = true;
			const firstStudent = Object.values(studentdata)[0];
			console.log('SIZE OF STUDENT DATA: ', Object.values(studentdata).length);
			let firstGrade = Number(firstStudent['student']['grade'])
				? Number(firstStudent['student']['grade'])
				: firstStudent['student']['grade'];

			for (const student of studentdata) {
				const grade = student['student'].grade;
				let parsedGrade = Number(grade) ? Number(grade) : grade;

				if (parsedGrade != firstGrade) {
					isHomogenous = false;
					break;
				}
			}

			if (isHomogenous) {
				this.allColumns.gradeColumn.visible = false;
			} else {
				this.allColumns.gradeColumn.visible = true;
			}
			//We don't saveTableState here because this happens when the user has not made any changes themselves.
		}
	}

	private setTargetStudentData(data: any) {
		let returnData: any = [];

		for (const student in data) {
			//if(data[student]['highestChallenge']['phase'] == this.targetPhase || ( (this.targetPhase == 1)&&(data[student]['highestChallenge']['phase'] == null) )){
			let oneStudent = [];
			oneStudent['highestChallenge'] = data[student]['highestChallenge'];
			oneStudent['id'] = data[student]['id'];
			oneStudent['previousIds'] = data[student]['previousIds'];
			oneStudent['previousScores'] = data[student]['previousScores'];
			oneStudent['student'] = data[student]['student'];
			oneStudent['totalTimesAssessed'] = data[student]['timesAssessed']; //count across all phases
			oneStudent['totalLastAssessed'] = data[student]['lastAssessed']; //most recent assessment in any phase
			oneStudent['scores'] = [];

			for (const book in data[student]['phaseData']) {
				//assumes only one book for now
				if (data[student]['phaseData'][book]['phaseNumber'] == this.targetPhase) {
					oneStudent['scores'] = data[student]['phaseData'][book]['scores'];
					break; //Currently just grabbing first assessment book for phase. We only have one currently.
				}
			}
			returnData.push(oneStudent);
			//}
		}
		return returnData;
	}

	private getTargetReportData(data) {
		let returnData = [];
		returnData['challenges'] = [];
		returnData['challengeCount'] = [];
		returnData['studentData'] = [];

		let returnChallenges = [];
		const returnChallengeCount = data['challengeCount']['Phase ' + this.targetPhase];
		const returnStudentData = data['studentData']; //this contains all phases, unlike our local studentData which is formated to be phase specific by setTargetData

		const book = Object.keys(data['challenges']['Phase ' + this.targetPhase])[0]; //use first assessment book. Currently only.
		returnChallenges = data['challenges']['Phase ' + this.targetPhase][book]['bookChallenges'];

		returnData['challenges'] = returnChallenges;
		returnData['challengeCount'] = returnChallengeCount;
		returnData['studentData'] = returnStudentData;

		return returnData;
	}

	ngOnDestroy() {
		this.subscriptions.forEach((sub) => sub.unsubscribe());
	}
	goToDash(student: Student) {
		localStorage.setItem('selectedStudent', JSON.stringify(student));
		localStorage.removeItem('activePhase');
		this.router.navigate(['dashboard']);
	}

	onFilterInput(event: any) {
		const value = event.target.value;
		this.filterTextChange.emit(value);
	}

	clearFilter() {
		this.filterTextChange.emit('');
	}

	public setBorder(challengeData, k) {
		if (challengeData.challenges[k].testId != challengeData.challenges[k - 1]?.testId) {
			return 'border-left-' + challengeData.challenges[k].testId;
		}
	}

	public setCellClass(eachStudent, eachChallenge) {
		if (eachStudent.scores[eachChallenge.id]?.baseLine && eachStudent.scores[eachChallenge.id]?.type == this.scoreType.PROGRESS) {
			return 'progress-cell-base';
		}
	}

	public saveUserTableStateToParent() {
		this.outgoingSorting.emit(this.allColumns);
	}

	public toggleHideColumn(target: SortableColumn) {
		for (const oneColumn of this.hidableColumns) {
			//hidableColumns is pointing at this.allColumns and therefore edits it's contents
			if (oneColumn == target) {
				oneColumn.visible = !oneColumn.visible;
				this.saveUserTableStateToParent(); //saving state after changing visibility

				if (oneColumn.visible == false && oneColumn.selected == true) {
					oneColumn.selected = false;

					// Find a visible column to select (prefer lastNameColumn, then firstNameColumn, then any visible)
					let newSelected = this.allColumns.lastNameColumn?.visible
						? this.allColumns.lastNameColumn
						: this.allColumns.firstNameColumn?.visible
						? this.allColumns.firstNameColumn
						: Object.values(this.allColumns).find((column) => column.visible === true);

					if (newSelected != undefined) {
						this.setSelectedColumn(newSelected);
					} else {
						// If no visible column found, default to lastNameColumn (which is always visible)
						if (this.allColumns.lastNameColumn) {
							this.setSelectedColumn(this.allColumns.lastNameColumn);
						}
					}
				}
			}
		}

		this.setFooterColumnCounts();
	}

	public setSelectedColumn(target: SortableColumn) {
		for (const column in this.allColumns) {
			if (this.allColumns[column] != target) {
				this.allColumns[column].selected = false;
				this.allColumns[column].isAscending = false; //Ensures other columns always ascend on first click
			} else {
				this.allColumns[column].selected = true;
			}
		}
		target.isAscending = !target.isAscending;
		this.allColumns.challengeIdColumn.selectedChallengeId = null;
		this.saveUserTableStateToParent();
		this.startSort();
	}

	public setChallengeSort(challengeIdNumber: number) {
		for (const column in this.allColumns) {
			if (this.allColumns[column] != this.allColumns.challengeIdColumn) {
				this.allColumns[column].selected = false;
				this.allColumns[column].isAscending = false; //Ensures other columns always ascend on first click
			}
		}
		this.allColumns.challengeIdColumn.selected = true;

		//Only toggle order if we are selecting the same challenge again
		if (
			challengeIdNumber == this.allColumns.challengeIdColumn.selectedChallengeId ||
			this.allColumns.challengeIdColumn.selectedChallengeId === null
		)
			this.allColumns.challengeIdColumn.isAscending = !this.allColumns.challengeIdColumn.isAscending;

		this.allColumns.challengeIdColumn.selectedChallengeId = challengeIdNumber;
		this.saveUserTableStateToParent();
		this.startSort();
	}

	private applyFilter() {
		if (!this.originalStudentData) {
			return;
		}

		if (!this.filterText || this.filterText.trim() === '') {
			this.studentData = [...this.originalStudentData];
		} else {
			const filterLower = this.filterText.toLowerCase().trim();
			this.studentData = this.originalStudentData.filter((student: any) => {
				const firstName = (student.student?.fname || '').toLowerCase();
				const lastName = (student.student?.lname || '').toLowerCase();
				const fullName = `${firstName} ${lastName}`;
				return firstName.includes(filterLower) || lastName.includes(filterLower) || fullName.includes(filterLower);
			});
		}

		this.classPhaseSize = this.studentData.length;

		// Only sort and set footer counts if columns are initialized
		if (this.allColumns) {
			this.startSort();
			this.setFooterColumnCounts();
		}
	}

	private startSort() {
		// If no column is selected, try to select a visible one
		const hasSelectedColumn = Object.values(this.allColumns).some((column) => column.selected === true);
		if (!hasSelectedColumn) {
			// Find and select the first visible column
			let columnToSelect = this.allColumns.lastNameColumn?.visible
				? this.allColumns.lastNameColumn
				: this.allColumns.firstNameColumn?.visible
				? this.allColumns.firstNameColumn
				: Object.values(this.allColumns).find((column) => column.visible === true);

			if (columnToSelect) {
				this.setSelectedColumn(columnToSelect);
				return; // setSelectedColumn will call startSort() again
			} else {
				// If no visible columns at all, default to lastNameColumn and make it visible
				if (this.allColumns.lastNameColumn) {
					this.allColumns.lastNameColumn.visible = true;
					this.setSelectedColumn(this.allColumns.lastNameColumn);
					return;
				}
				// If we still can't find anything, just return without sorting
				return;
			}
		}

		switch (true) {
			case this.allColumns.lastNameColumn.selected:
				this.sortNames('lname', 'fname', this.allColumns.lastNameColumn.isAscending);
				break;
			case this.allColumns.firstNameColumn.selected:
				this.sortNames('fname', 'lname', this.allColumns.firstNameColumn.isAscending);
				break;
			case this.allColumns.gradeColumn.selected:
				this.sortGrades();
				break;
			case this.allColumns.timesAssessedColumn.selected:
				this.sortAssessNums();
				break;
			case this.allColumns.dateAssessedColumn.selected:
				this.sortDateAssess();
				break;
			case this.allColumns.challengeIdColumn.selected:
				this.sortByChallengeId(this.allColumns.challengeIdColumn.selectedChallengeId);
				break;
			case this.allColumns.allChallengesColumn.selected:
				this.sortAllChallenges();
				break;
			default:
				// Fallback: try to select lastNameColumn if visible
				if (this.allColumns.lastNameColumn?.visible) {
					this.setSelectedColumn(this.allColumns.lastNameColumn);
				} else if (this.allColumns.firstNameColumn?.visible) {
					this.setSelectedColumn(this.allColumns.firstNameColumn);
				}
		}
	}

	private sortNames(sortName: string, secondSortName: string, isAscending: boolean) {
		//sortName and secondSortName are simply the key that points to the first and last names (ex: 'fname' and 'lname').
		//The order of the keys entered depends on which we're sorting by, first or last names.

		if (!this.studentData) return;
		this.studentData = this.studentData.sort((a, b) => {
			return this.sortTwoNames(a, b, isAscending, sortName, secondSortName);
		});
	}

	private sortTwoNames(studentA: any, studentB: any, ascending: boolean, primaryName: string, secondaryName: string) {
		//Returns the appropriate number for a .sort algorithm to be given.
		if (ascending) {
			return (
				studentA.student[primaryName].localeCompare(studentB.student[primaryName]) ||
				studentA.student[secondaryName].localeCompare(studentB.student[secondaryName])
			);
		} else {
			return (
				studentB.student[primaryName].localeCompare(studentA.student[primaryName]) ||
				studentB.student[secondaryName].localeCompare(studentA.student[secondaryName])
			);
		}
	}

	private sortGrades() {
		if (!this.studentData) return;
		this.studentData.sort((a, b) => {
			//Switch a and b if not ascending, to get the same result as writing two sorts
			const aGrade = this.allColumns.gradeColumn.isAscending ? a.student.grade : b.student.grade;
			const bGrade = this.allColumns.gradeColumn.isAscending ? b.student.grade : a.student.grade;

			const isANull = aGrade === null || aGrade === '';
			const isBNull = bGrade === null || bGrade === '';

			if (isANull && isBNull) {
				return this.sortTwoNames(a, b, true, 'lname', 'fname');
			} else if (isANull) {
				return 1;
			} else if (isBNull) {
				return -1;
			}
			//If here, neither are Null

			const isANumber = isFinite(Number(aGrade));
			const isBNumber = isFinite(Number(bGrade));

			if (isANumber && isBNumber) {
				//Both are numbers
				let result = Number(aGrade) - Number(bGrade);
				if (result == 0) {
					return this.sortTwoNames(a, b, true, 'lname', 'fname');
				} else {
					return result;
				}
			} else if (!isANumber && !isBNumber) {
				//Both are letters
				return aGrade.localeCompare(bGrade);
			} else {
				//only one is a letter
				return isANumber ? 1 : -1;
			}
		});
	}

	private sortAssessNums() {
		if (!this.studentData) return;
		if (this.allColumns.timesAssessedColumn.isAscending) {
			this.studentData.sort((a, b) => {
				//Catch if value is undefined or weird
				let aTotal = isNaN(Number(a.totalTimesAssessed)) ? 0 : Number(a.totalTimesAssessed);
				let bTotal = isNaN(Number(b.totalTimesAssessed)) ? 0 : Number(b.totalTimesAssessed);

				return aTotal - bTotal;
			});
		} else {
			this.studentData.sort((a, b) => {
				let aTotal = isNaN(Number(a.totalTimesAssessed)) ? 0 : Number(a.totalTimesAssessed);
				let bTotal = isNaN(Number(b.totalTimesAssessed)) ? 0 : Number(b.totalTimesAssessed);

				return bTotal - aTotal;
			});
		}
	}

	private sortDateAssess() {
		if (!this.studentData) return;
		if (this.allColumns.dateAssessedColumn.isAscending) {
			this.studentData.sort((a, b) => {
				if (isNaN(Date.parse(a.totalLastAssessed))) {
					return 1;
				} else if (isNaN(Date.parse(b.totalLastAssessed))) {
					return -1;
				}
				return Date.parse(a.totalLastAssessed) - Date.parse(b.totalLastAssessed);
			});
		} else {
			//is Descending
			this.studentData.sort((a, b) => {
				if (isNaN(Date.parse(a.totalLastAssessed))) {
					return -1;
				} else if (isNaN(Date.parse(b.totalLastAssessed))) {
					return 1;
				}
				return Date.parse(b.totalLastAssessed) - Date.parse(a.totalLastAssessed);
			});
		}
	}

	private sortByChallengeId(challengeId: number) {
		if (!this.studentData) return;
		this.studentData.sort((a, b) => {
			let aScore = Number(a.scores[this.allColumns.challengeIdColumn.selectedChallengeId]?.score);
			let bScore = Number(b.scores[this.allColumns.challengeIdColumn.selectedChallengeId]?.score);
			if (isNaN(aScore)) aScore = 0;
			if (isNaN(bScore)) bScore = 0;

			if (aScore == bScore) {
				return this.sortTwoNames(a, b, true, 'lname', 'fname');
			} else {
				if (this.allColumns.challengeIdColumn.isAscending) {
					return aScore - bScore;
				} else {
					return bScore - aScore;
				}
			}
		});
	}

	private sortAllChallenges() {
		if (!this.studentData) return;
		this.studentData.sort((a, b) => {
			let aHighest: number;
			let aPhase: number;
			let aPassed: boolean;

			let bHighest: number;
			let bPhase: number;
			let bPassed: boolean;

			/* Data Collection: */
			aHighest = Number(a['highestChallenge']['challengeId']);
			aPassed = Number(a['highestChallenge']['score']) >= 3; //Students with no passing-score challenges, are positioned at their highest attempted challenge
			aPhase = a['highestChallenge']['phase'] ? Number(a['highestChallenge']['phase']) : 1;

			bHighest = Number(b['highestChallenge']['challengeId']);
			bPassed = Number(b['highestChallenge']['score']) >= 3;
			bPhase = b['highestChallenge']['phase'] ? Number(b['highestChallenge']['phase']) : 1;

			/* Sorting: */
			if (aPhase != bPhase) {
				//If students are in different Phases from eachother

				if (this.allColumns.allChallengesColumn.isAscending) {
					return aPhase - bPhase;
				} else {
					return bPhase - aPhase;
				}
			} else {
				//else, if both students are in the same phase, use highestChallenge sorting below

				//Sorts on highest challengeId achieved. Should not reach this if they're in different phases from one another.
				if (aHighest == bHighest) {
					if (aPassed != bPassed) {
						//Sorts students with no passing challenges, below equal-challenge passing-score students.

						return this.allColumns.allChallengesColumn.isAscending ? Number(aPassed) - Number(bPassed) : Number(bPassed) - Number(aPassed);
					} else {
						return this.sortTwoNames(a, b, true, 'lname', 'fname');
					}
				} else {
					return this.allColumns.allChallengesColumn.isAscending ? aHighest - bHighest : bHighest - aHighest;
				}
			}
		});
	}
}
